// src/components/ShareDossierButton.jsx
// Phase 3 (§6/§17) — créer un partage institutionnel TEMPORAIRE, CIBLÉ et AUDITÉ d'un dossier.
// Le collectionneur reste maître : durée, destinataire, périmètre. Insert RLS (owner).
// Génère un lien public /dossier/:token (lecture via edge dossier-share, journalisée).
import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const RECIPIENTS = [
  { value: "insurer", label: "Assureur" },
  { value: "notaire", label: "Notaire" },
  { value: "bank", label: "Banque / banque privée" },
  { value: "expert", label: "Expert" },
  { value: "family_office", label: "Family Office" },
  { value: "other", label: "Autre" },
];
const DOSSIERS = [
  { value: "full", label: "Dossier complet" },
  { value: "succession", label: "Succession" },
  { value: "insurance", label: "Assurance" },
];
const DURATIONS = [
  { value: 7, label: "7 jours" },
  { value: 30, label: "30 jours" },
  { value: 90, label: "90 jours" },
];

export default function ShareDossierButton({ scopeType = "collection", scopeId, collectionName = "", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [recipientKind, setRecipientKind] = useState("insurer");
  const [recipientLabel, setRecipientLabel] = useState("");
  const [dossierType, setDossierType] = useState("full");
  const [days, setDays] = useState(30);
  const [showOwner, setShowOwner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const reset = () => { setUrl(""); setError(""); setCopied(false); };

  const create = async () => {
    if (!scopeId || !user?.id) return;
    setSaving(true); setError("");
    try {
      const { data, error: e } = await supabase
        .from("dossier_shares")
        .insert({
          client_id: user.id,
          scope_type: scopeType,
          scope_id: scopeId,
          dossier_type: dossierType,
          recipient_kind: recipientKind,
          recipient_label: recipientLabel.trim() || null,
          show_owner: showOwner,
          expires_at: new Date(Date.now() + days * 86400000).toISOString(),
        })
        .select("token")
        .single();
      if (e) throw e;
      setUrl(`${window.location.origin}/dossier/${data.token}`);
    } catch (e) {
      setError(e.message || "Création impossible.");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); } catch { /* noop */ } };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        disabled={!scopeId}
        className={className || "inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"}
      >
        Partager le dossier (lien sécurisé)
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="font-serif text-lg font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>Partage institutionnel</h3>
                <p className="mt-0.5 text-sm text-gray-500">{collectionName || "Dossier"} — temporaire, ciblé, journalisé.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-black">✕</button>
            </div>

            {!url ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Destinataire</span>
                    <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={recipientKind} onChange={(e) => setRecipientKind(e.target.value)}>
                      {RECIPIENTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Type de dossier</span>
                    <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={dossierType} onChange={(e) => setDossierType(e.target.value)}>
                      {DOSSIERS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Institution (optionnel)</span>
                  <input className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Ex : Étude Maître Dupont, AXA Art…" value={recipientLabel} onChange={(e) => setRecipientLabel(e.target.value)} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Durée d'accès</span>
                  <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm md:w-48" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                    {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="mt-0.5" checked={showOwner} onChange={(e) => setShowOwner(e.target.checked)} />
                  <span>Afficher mon nom au destinataire <span className="text-gray-500">(par défaut, le dossier reste anonyme)</span></span>
                </label>
                {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
                <div className="flex justify-end gap-3 pt-1">
                  <button onClick={() => setOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Annuler</button>
                  <button onClick={create} disabled={saving} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60">
                    {saving ? "Création…" : "Créer le lien"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Lien d'accès (expire dans {days} jours). Il ne donne accès qu'à ce dossier, et chaque consultation est journalisée.</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={url} className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-xs" />
                  <button onClick={copy} className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800">{copied ? "Copié ✓" : "Copier"}</button>
                </div>
                <div className="flex justify-end pt-1">
                  <button onClick={() => setOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Fermer</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
